<script setup lang="ts">
import axios from 'axios'
import { computed, ref } from 'vue'
import type { SupabaseSession, SupabaseUser } from '../types'

const emit = defineEmits(['success'])
const API_URL = import.meta.env.DEV
  ? 'http://localhost:8040/auth/register/'
  : 'https://api.valveilla.net/auth/register/'

interface RegisterForm {
  email: string
  password: string
  passwordConfirm: string
  displayName: string
}

const form = ref<RegisterForm>({
  email: '',
  password: '',
  passwordConfirm: '',
  displayName: '',
})

const error = ref('')
const isLoading = ref(false)

const passwordsMatch = computed(() => form.value.password === form.value.passwordConfirm && form.value.password !== '')
const passwordMinLength = computed(() => form.value.password.length > 7)

async function handleSubmit() {
  if (isLoading.value) {
    return
  }
  if (!passwordsMatch.value) {
    error.value = 'Passwords do not match'
    return
  }

  error.value = ''
  isLoading.value = true

  try {
    const response = await axios.post<{ user: SupabaseUser; session: SupabaseSession }>(API_URL, {
      email: form.value.email,
      password: form.value.password,
      display_name: form.value.displayName,
    })

    // Store auth in browser
    localStorage.setItem('user', JSON.stringify(response.data.user))
    localStorage.setItem('session', JSON.stringify(response.data.session))

    // Emit success for parent handling
    emit('success', response.data.user)
  } catch (err) {
    if (axios.isAxiosError(err)) {
      error.value = err.response?.data?.detail || 'Registration failed'
    } else {
      error.value = 'An unexpected error occurred'
    }
  } finally {
    isLoading.value = false
  }
}

console.log('RegisterUser component loaded. API_URL:', API_URL)
</script>

<template>
  <h2>Rekisteröidy</h2>

  <p>Kaikki kentät tarvitaan</p>

  <form @submit.prevent="handleSubmit" class="space-y-4 max-w-md mt-4">
    <div v-if="error" class="p-3 text-red-700 bg-red-100 rounded-sm">
      {{ error }}
    </div>

    <div class="space-y-2">
      <label for="email" class="block text-sm font-medium">Sähköpostiosoite</label>
      <input
        id="email"
        v-model="form.email"
        type="email"
        required
        class="w-full p-2 border rounded-sm focus:ring-2 focus:ring-blue-500"
      />
    </div>

    <div class="space-y-2">
      <label for="displayName" class="block text-sm font-medium">Nimimerkki tai nimi (julkinen)</label>
      <input
        id="displayName"
        v-model="form.displayName"
        type="text"
        required
        class="w-full p-2 border rounded-sm focus:ring-2 focus:ring-blue-500"
      />
    </div>

    <div class="space-y-2">
      <label for="password" class="block text-sm font-medium">Salasana</label>
      <input
        id="password"
        v-model="form.password"
        type="password"
        required
        minlength="8"
        class="w-full p-2 border rounded-sm focus:ring-2 focus:ring-blue-500"
      />
    </div>

    <div class="space-y-2">
      <label for="passwordConfirm" class="block text-sm font-medium">Vahvista salasana</label>
      <input
        id="passwordConfirm"
        v-model="form.passwordConfirm"
        type="password"
        required
        minlength="8"
        class="w-full p-2 border rounded-sm focus:ring-2 focus:ring-blue-500"
      />
      <p v-if="form.password.length && form.passwordConfirm && !passwordsMatch">Salasanat eivät täsmää</p>
      <p v-if="form.password.length && form.passwordConfirm && !passwordMinLength">
        Salasanassa täytyy olla vähintään 8 merkkiä
      </p>
    </div>

    <button
      type="submit"
      :disabled="isLoading || !passwordsMatch || !passwordMinLength"
      class="w-full py-2 px-4 bg-blue-600 text-white rounded-sm hover:bg-blue-700 disabled:opacity-50"
    >
      {{ isLoading ? 'Rekisteröidään...' : 'Rekisteröidy' }}
    </button>
  </form>
</template>
